import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { useSpacesForFilterQuery } from './__generated__/useSpacesForFilterQuery.graphql.ts';

const DEFAULT_SPACES_LIMIT = 200;

export type SpaceForFilter = {
	id: string;
	name: string;
	iconPath?: string;
};

/**
 * Fetches all spaces the current user has access to via the Spaces GraphQL API.
 * Used to populate the Space filter dropdown in the Content Metrics tab with
 * space icons/emoji alongside space names.
 *
 * Uses cache-first fetch policy since the space list is stable and changes infrequently.
 */
export const useSpacesForFilter = () => {
	const { cloudId } = useSessionData();

	const { data, loading, error } = useQueryInterop<
		useSpacesForFilterQuery['response'],
		useSpacesForFilterQuery['variables']
	>(
		graphql`
			query useSpacesForFilterQuery($first: Int!) {
				spaces(first: $first, status: "current") {
					nodes {
						id
						name
						icon {
							path
						}
					}
				}
			}
		`,
		{
			variables: { first: DEFAULT_SPACES_LIMIT },
			skip: !cloudId,
			fetchPolicy: 'cache-first',
		},
	);

	const spaces: SpaceForFilter[] = useMemo(() => {
		const nodes = data?.spaces?.nodes ?? [];
		return nodes.reduce<SpaceForFilter[]>((acc, node) => {
			if (node != null && node.id && node.name) {
				acc.push({
					id: node.id,
					name: node.name,
					iconPath: node.icon?.path ?? undefined,
				});
			}
			return acc;
		}, []);
	}, [data]);

	return {
		spaces,
		loading,
		error,
	};
};
